import { useState, useEffect, useRef } from 'react'
import clamp from 'lodash/clamp'
import toNumber from 'lodash/toNumber'
import isNumber from 'lodash/isNumber'
import round from 'lodash/round'
import axios from 'axios'

export const useWindowSize = () => {
  // Initialize state with undefined width/height so server and client renders match
  // Learn more here: https://joshwcomeau.com/react/the-perils-of-rehydration/
  const [windowSize, setWindowSize] = useState({
    width: undefined,
    height: undefined,
  })

  useEffect(() => {
    function handleResize() {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      })
    }
    window.addEventListener('resize', handleResize)
    handleResize()
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  return windowSize
}

export const useLocalStorage = (key, initialValue) => {
  const [storedValue, setStoredValue] = useState(() => {
    try {
      const item = window.localStorage.getItem(key)
      return item ? JSON.parse(item) : initialValue
    } catch (err) {
      return initialValue
    }
  })

  const setValue = (value) => {
    const valueToStore = value instanceof Function ? value(storedValue) : value
    setStoredValue(valueToStore)
    window.localStorage.setItem(key, JSON.stringify(valueToStore))
  }

  return [storedValue, setValue]
}

export const useAreas = () => {
  const [areas, setAreas] = useState([])

  useEffect(() => {
    ;(async () => {
      try {
        const { data } = await axios.get('/api/get-areas')

        setAreas(data)
      } catch (error) {
        setAreas([])
      }
    })()
  }, [])

  return areas
}

const useValidateNumber = (value, min, max) => {
  let number = value

  if (!isNumber(number)) {
    number = number.replace(',', '.')
    number = toNumber(number)
  }

  if (min && max) {
    number = clamp(number, min, max)
  }

  number = round(number, 1)

  let string = number.toString()

  if (
    typeof value === 'number' ||
    (typeof value === 'string' && value.includes(','))
  ) {
    string = string.replace('.', ',')
  }

  return [number, string]
}

export const useInputSlider = (initialValue, min, max) => {
  const [inputValue, setInputValue] = useState(initialValue)
  const [sliderValue, setSliderValue] = useState(initialValue)

  const handleSliderChange = (value) => {
    const [number, string] = useValidateNumber(value[0], min, max)
    setInputValue(string)
    setSliderValue(number)
  }

  const handleInputChange = (value) => {
    // Only allow digits, comma and period
    const regex = new RegExp('^[0-9]+[.,]?[0-9]*$|^$')
    if (!regex.test(value)) return

    // Is last character comma or period?
    const lastCommaOrPeriod = new RegExp('[.,]$')

    if (lastCommaOrPeriod.test(value)) {
      setInputValue(value)
    } else {
      const [number, string] = useValidateNumber(value)

      if (number < min || number > max) {
        setInputValue(string)
      } else {
        setInputValue(string)
        setSliderValue(number)
      }
    }
  }

  const handleInputBlur = (value) => {
    const [number, string] = useValidateNumber(value, min, max)
    setInputValue(string)
    setSliderValue(number)
  }

  return [
    inputValue,
    sliderValue,
    handleSliderChange,
    handleInputChange,
    handleInputBlur,
  ]
}

export const useInterval = (callback, delay) => {
  const intervalRef = useRef(null)
  const savedCallback = useRef(callback)
  useEffect(() => {
    savedCallback.current = callback
  }, [callback])
  useEffect(() => {
    const tick = () => savedCallback.current()
    if (typeof delay === 'number') {
      intervalRef.current = window.setInterval(tick, delay)
      return () => window.clearInterval(intervalRef.current)
    }
  }, [delay])
  return intervalRef
}

// https://blog.logrocket.com/fixing-gatsbys-rehydration-issue/
// https://www.joshwcomeau.com/react/the-perils-of-rehydration/
export const useHasMounted = () => {
  const [hasMounted, setHasMounted] = useState(false)

  useEffect(() => {
    setHasMounted(true)
  }, [])

  return hasMounted
}

export const useExtendedState = (initialState) => {
  const [state, setState] = useState(initialState);
  const getLatestState = () => {
    return new Promise((resolve, reject) => {
      setState((s) => {
        resolve(s)
        return s
      })
    })
  }
  return [state, setState, getLatestState]
}
